import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, authHeaders } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Download, ChevronDown, ChevronUp, Search, UserRound } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["NEW", "CONTACTED", "QUOTATION SENT", "NEGOTIATION", "SAMPLE REQUESTED", "ORDER CONFIRMED", "CLOSED", "LOST"];

const statusColor = (s) => {
  const k = (s || "NEW").toUpperCase();
  if (k === "NEW") return "bg-[#D4AF37]/20 text-[#8a6d1a]";
  if (k === "ORDER CONFIRMED") return "bg-[#004225] text-white";
  if (k === "LOST") return "bg-red-100 text-red-700";
  if (k === "CLOSED") return "bg-gray-200 text-gray-600";
  return "bg-[#F7F2E2] text-[#004225]";
};

const AdminEnquiries = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fCountry, setFCountry] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const countries = [...new Set(rows.map((r) => r.country).filter(Boolean))].sort();
  const filtered = rows.filter((r) => {
    if (fCountry !== "all" && r.country !== fCountry) return false;
    if (fStatus !== "all" && (r.status || "NEW").toUpperCase() !== fStatus) return false;
    if (fFrom && (r.created_at || "") < fFrom) return false;
    if (fTo && (r.created_at || "") > `${fTo}T23:59`) return false;
    if (search) {
      const hay = `${r.name} ${r.company} ${r.email} ${r.phone} ${(r.items || []).map((i) => i.product_name).join(" ")}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const load = () => {
    axios.get(`${API}/enquiries`, authHeaders())
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Could not load enquiries"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}/status`, { status }, authHeaders());
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Status updated to ${status}`);
    } catch (e) {
      toast.error("Status update failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/enquiries.csv`, { ...authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "gn-enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  if (loading) return <div className="text-[#171717]/60 text-sm">Loading enquiries…</div>;

  return (
    <div data-testid="admin-enquiries">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="font-display text-xl text-[#004225] mr-auto">Buyer Enquiries ({filtered.length}/{rows.length})</div>
        <Button onClick={exportCsv} data-testid="export-csv-btn" variant="outline" className="rounded-full text-[11px] uppercase tracking-widest">
          <Download className="h-3.5 w-3.5 mr-2" />Export CSV
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="enquiry-filters">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#171717]/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="filter-search" placeholder="Buyer, company, product…" className="pl-8 w-56 bg-white" />
        </div>
        <Select value={fCountry} onValueChange={setFCountry}>
          <SelectTrigger className="w-40 bg-white" data-testid="filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            {countries.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-48 bg-white" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="filter-from" className="w-40 bg-white" />
        <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="filter-to" className="w-40 bg-white" />
      </div>
      {rows.length === 0 && <div className="bg-white border border-[#004225]/10 rounded-sm p-10 text-center text-[#171717]/60">No enquiries yet.</div>}
      {rows.length > 0 && filtered.length === 0 && <div className="bg-white border border-[#004225]/10 rounded-sm p-10 text-center text-[#171717]/60">No enquiries match the current filters.</div>}
      <div className="space-y-2">
        {filtered.map((e) => (
          <div key={e.id} className="bg-white border border-[#004225]/10 rounded-sm" data-testid={`enquiry-row-${e.id}`}>
            <div className="p-4 flex flex-wrap items-center gap-3 cursor-pointer" onClick={() => setOpen(open === e.id ? null : e.id)}>
              <div className="text-[11px] text-[#171717]/50 w-24">{(e.created_at || "").slice(0, 10)}</div>
              <div className="flex-1 min-w-[160px]">
                <div className="font-semibold text-[#004225] text-sm">{e.name} — {e.company}</div>
                <div className="text-[12px] text-[#171717]/60">{e.country} · {e.email}</div>
              </div>
              <div className="text-[12px] text-[#171717]/70">{e.items?.length ? `${e.items.length} product(s)` : "Direct enquiry"}</div>
              <span className={`text-[10px] font-eyebrow px-2.5 py-1 rounded-full ${statusColor(e.status)}`}>{(e.status || "NEW").toUpperCase()}</span>
              <Link to={`/admin/enquiries/${e.id}`} data-testid={`open-enquiry-${e.id}`} onClick={(ev) => ev.stopPropagation()} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-[#004225] border border-[#004225]/20 rounded-full px-3 py-1.5 hover:bg-[#F7F2E2]"><UserRound className="h-3 w-3" />Profile</Link>
              {open === e.id ? <ChevronUp className="h-4 w-4 text-[#171717]/40" /> : <ChevronDown className="h-4 w-4 text-[#171717]/40" />}
            </div>
            {open === e.id && (
              <div className="px-4 pb-4 pt-2 border-t border-[#004225]/10 grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-1.5">
                  <div><span className="text-[#171717]/50">Phone/WhatsApp:</span> <a className="text-[#004225] font-medium" href={`tel:${e.phone}`}>{e.phone}</a></div>
                  <div><span className="text-[#171717]/50">Destination Port:</span> {e.destination_port || "—"}</div>
                  <div><span className="text-[#171717]/50">Incoterm:</span> {e.incoterm || "—"}</div>
                  <div><span className="text-[#171717]/50">Packaging:</span> {e.packaging || "—"}</div>
                  <div><span className="text-[#171717]/50">Delivery:</span> {e.delivery_timeline || "—"}</div>
                  <div><span className="text-[#171717]/50">Source:</span> {e.source || "—"}{e.page ? ` · ${e.page}` : ""}</div>
                  {e.message && <div className="pt-1"><span className="text-[#171717]/50">Message:</span><p className="mt-1 text-[#171717]/80 whitespace-pre-wrap">{e.message}</p></div>}
                </div>
                <div>
                  <div className="text-[10px] font-eyebrow text-[#B5942D] mb-2">Requested Products</div>
                  {e.items?.length ? e.items.map((i, idx) => (
                    <div key={idx} className="py-1.5 border-b border-[#004225]/5 text-[13px]">
                      <span className="font-medium text-[#004225]">{i.product_name}</span>
                      <span className="text-[#171717]/60"> — {i.quantity_mt || "?"} MT · {i.packaging || "pkg TBC"} · {i.incoterm || ""} {i.destination_port || ""}</span>
                    </div>
                  )) : <div className="text-[13px] text-[#171717]/50">No cart items</div>}
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-[10px] font-eyebrow text-[#B5942D]">Set Status</span>
                    <Select value={(e.status || "NEW").toUpperCase()} onValueChange={(v) => setStatus(e.id, v)}>
                      <SelectTrigger className="w-56" data-testid={`status-select-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminEnquiries;
